import { micrositesMetadataList } from '../CMSMicrosite/constants'
import { CMS_MICROSITE_MESSAGE_KEY } from '../CMSMicrosite/messages/messages'
import { getMicrositePathwayKey } from '../CMSMicrosite/utils'
import { ESSENTIALS_EVENTS_PAGE } from '../common/constants/constants'
import { ProgramCustomerPropertyId } from '../common/constants/customerProgram'

/** Use this to set custom pathways such as from cms toolkits */
export type CustomPathwayOption = `CustomPathway${string}`

/**
 * Registration pathways from CMS microsite toolkits
 *
 * Note: It's ok to not use useActiveMicrositesMetadataList here because it's fine if an inactive site is added to this list, it just won't be used.
 */
const toolkitPathways = micrositesMetadataList.reduce<{ key: string; redirectUrl: string }[]>((result, curMetadata) => {
  const webinarEvents = curMetadata.postMessageEvents.filter(
    (item) => item.key === CMS_MICROSITE_MESSAGE_KEY.REGISTER_WEBINAR,
  )

  webinarEvents.forEach((webinarEvent) => {
    result.push({
      key: getMicrositePathwayKey(
        curMetadata.micrositeId,
        CMS_MICROSITE_MESSAGE_KEY.REGISTER_WEBINAR,
        webinarEvent.params?.eventKey,
      ),
      redirectUrl: webinarEvent.params?.webinarLink ?? ESSENTIALS_EVENTS_PAGE,
    })
  })

  const webinarMainPageEvent = curMetadata.postMessageEvents.find(
    (item) => item.key === CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_WEBINAR_EVENTS,
  )
  if (webinarMainPageEvent) {
    const key = getMicrositePathwayKey(curMetadata.micrositeId, CMS_MICROSITE_MESSAGE_KEY.NAVIGATE_TO_WEBINAR_EVENTS)
    const pathway = { key, redirectUrl: ESSENTIALS_EVENTS_PAGE }
    result.push(pathway)
  }
  return result
}, [])

/** Registration pathways from the homepage microsite/forked triage */
export enum PathwayOptions {
  SELF_SEARCH = 'self-search',
  CHILD_SEARCH = 'child-search',
  COACHING_TOOLKIT = 'coaching-toolkit',
  PARENTING_TOOLKIT = 'parenting-toolkit',
  ESSENTIALS = 'essentials',
  MEDICATION_CONSULTATION = 'medication-consultation',
  COUPLES_THERAPY = 'couples-therapy',
  FAMILY_THERAPY = 'family-therapy',
  WELLNESS_CHECK_IN = 'wellness-check-in',
  ONSITE_THERAPY = 'onsite-therapy',
  NEURODIVERSITY_CARE_OPTIONS = 'neurodiversity-care-options',
  NEURODIVERSITY_CARE_NAVIGATOR = 'neurodiversity-care-navigator',
  HEALTH_CHALLENGES_CARE_OPTIONS = 'health-challenges-care-options',
  WOMENS_HEALTH_CARE_OPTIONS = 'womens-health-care-options',
  ESSENTIALS_EVENTS = 'essentials-events',
}

export type ExtendedPathwayOption = PathwayOptions | CustomPathwayOption

/** If the pathway option is available on forked triage. Maps pathway option to boolean. Pathway option `key` is required in this map */
export const IS_PATHWAY_OPTION_ON_FORKED_TRIAGE: { [key in ExtendedPathwayOption]: boolean } = {
  [PathwayOptions.SELF_SEARCH]: true,
  [PathwayOptions.CHILD_SEARCH]: true,
  [PathwayOptions.COACHING_TOOLKIT]: true,
  [PathwayOptions.PARENTING_TOOLKIT]: true,
  [PathwayOptions.ESSENTIALS]: false,
  [PathwayOptions.MEDICATION_CONSULTATION]: true,
  [PathwayOptions.COUPLES_THERAPY]: true,
  [PathwayOptions.FAMILY_THERAPY]: true,
  [PathwayOptions.WELLNESS_CHECK_IN]: false,
  [PathwayOptions.ONSITE_THERAPY]: true,
  [PathwayOptions.NEURODIVERSITY_CARE_OPTIONS]: false,
  [PathwayOptions.NEURODIVERSITY_CARE_NAVIGATOR]: false,
  [PathwayOptions.HEALTH_CHALLENGES_CARE_OPTIONS]: false,
  [PathwayOptions.WOMENS_HEALTH_CARE_OPTIONS]: false,
  [PathwayOptions.ESSENTIALS_EVENTS]: false,
}

export enum PathwaysRedirectUrls {
  SELF_TRIAGE = '/secure/onboard/about',
  CHILD_TRIAGE = '/secure/onboard/about-child',
  COACHING_TOOLKIT = '/secure/wellness/toolkit/purpose',
  PARENTING_TOOLKIT = '/secure/wellness/toolkit/parent',
  ESSENTIALS = '/secure/index/essentials',
  MEDICATION_CONSULTATION = '/secure/onboard/match-location?directPath=true&clientele=Individual&treatment=MedicationManagement&partner=BlendedCare&offering=Default&directLinkIntent=DIRECT_LINK_MEDS_CONSULT',
  WELLNESS_CHECK_IN = '/secure/wellness-check-in/results',
  COUPLES_THERAPY = `/secure/onboard/match-location?directPath=true&clientele=Couples&treatment=Therapy&partner=DirectAccess&offering=Default&directLinkSource=microsite_forked_triage&directLinkIntent=DIRECT_LINK_INTENT_COUPLES`,
  FAMILY_THERAPY = `/pathways/care-options?selectedPathwayOption=${PathwayOptions.FAMILY_THERAPY}`,
  PARENTING_TOOLKIT_V2 = `/pathways/care-options?selectedPathwayOption=${PathwayOptions.PARENTING_TOOLKIT}`,
  MEDICATION_CONSULTATION_V2 = `/pathways/care-options?selectedPathwayOption=${PathwayOptions.MEDICATION_CONSULTATION}`,
  NEURODIVERSITY_CARE_OPTIONS = `/secure/center-of-excellence/neurodiversity/care-options`,
  NEURODIVERSITY_CARE_NAVIGATOR = `/secure/center-of-excellence/neurodiversity/contact-care-navigator`,
  HEALTH_CHALLENGES_CARE_OPTIONS = `/secure/health-challenges/care-options`,
  WOMENS_HEALTH_CARE_OPTIONS = `/secure/womens-health/care-options`,
  ESSENTIALS_EVENTS = '/secure/index/essentials/events',
}

const BasePathwayOptionToRedirectUrlMapping: { [key in ExtendedPathwayOption]?: string } = {
  [PathwayOptions.SELF_SEARCH]: PathwaysRedirectUrls.SELF_TRIAGE,
  [PathwayOptions.CHILD_SEARCH]: PathwaysRedirectUrls.CHILD_TRIAGE,
  [PathwayOptions.COACHING_TOOLKIT]: PathwaysRedirectUrls.COACHING_TOOLKIT,
  [PathwayOptions.ESSENTIALS]: PathwaysRedirectUrls.ESSENTIALS,
  [PathwayOptions.FAMILY_THERAPY]: PathwaysRedirectUrls.FAMILY_THERAPY,
  [PathwayOptions.COUPLES_THERAPY]: PathwaysRedirectUrls.COUPLES_THERAPY,
  [PathwayOptions.WELLNESS_CHECK_IN]: PathwaysRedirectUrls.WELLNESS_CHECK_IN,
  [PathwayOptions.NEURODIVERSITY_CARE_OPTIONS]: PathwaysRedirectUrls.NEURODIVERSITY_CARE_OPTIONS,
  [PathwayOptions.NEURODIVERSITY_CARE_NAVIGATOR]: PathwaysRedirectUrls.NEURODIVERSITY_CARE_NAVIGATOR,
  [PathwayOptions.HEALTH_CHALLENGES_CARE_OPTIONS]: PathwaysRedirectUrls.HEALTH_CHALLENGES_CARE_OPTIONS,
  [PathwayOptions.WOMENS_HEALTH_CARE_OPTIONS]: PathwaysRedirectUrls.WOMENS_HEALTH_CARE_OPTIONS,
  [PathwayOptions.ESSENTIALS_EVENTS]: PathwaysRedirectUrls.ESSENTIALS_EVENTS,
  ...Object.fromEntries(toolkitPathways.map((item) => [item.key, item.redirectUrl])),
}

export const PathwayOptionToRedirectUrlMapping: { [key in ExtendedPathwayOption]?: string } = {
  ...BasePathwayOptionToRedirectUrlMapping,
  [PathwayOptions.PARENTING_TOOLKIT]: PathwaysRedirectUrls.PARENTING_TOOLKIT,
  [PathwayOptions.MEDICATION_CONSULTATION]: PathwaysRedirectUrls.MEDICATION_CONSULTATION,
}

export const PathwayOptionToRedirectUrlMappingV2: { [key in ExtendedPathwayOption]?: string } = {
  ...BasePathwayOptionToRedirectUrlMapping,
  [PathwayOptions.PARENTING_TOOLKIT]: PathwaysRedirectUrls.PARENTING_TOOLKIT_V2,
  [PathwayOptions.MEDICATION_CONSULTATION]: PathwaysRedirectUrls.MEDICATION_CONSULTATION_V2,
}

/**
 * Care options that should be available to teens (ie teens should be directed to the care option once logged into the app)
 */
export const TEEN_ENABLED_PATHWAYS: ExtendedPathwayOption[] = [
  PathwayOptions.SELF_SEARCH,
  PathwayOptions.ESSENTIALS,
  PathwayOptions.WELLNESS_CHECK_IN,
  PathwayOptions.NEURODIVERSITY_CARE_NAVIGATOR,
  PathwayOptions.NEURODIVERSITY_CARE_OPTIONS,
]

/** Care options that are not available to international users */
export const INTERNATIONAL_DISABLED_PATHWAYS: ExtendedPathwayOption[] = [
  PathwayOptions.COUPLES_THERAPY,
  PathwayOptions.FAMILY_THERAPY,
  PathwayOptions.PARENTING_TOOLKIT,
]

export const PATHWAY_QUERY_PARAM = 'pathwayOption'
export const AUTO_ENROLLMENT_TOKEN = 'autoEnrollmentToken'

/**
 * Maps program customer property to available pathways
 */
export const PROGRAM_CUSTOMER_PROPERTY_TO_PATHWAY_MAP = new Map<ProgramCustomerPropertyId, ExtendedPathwayOption[]>([
  [ProgramCustomerPropertyId.considerLyraTherapy, [PathwayOptions.SELF_SEARCH]],
  [
    ProgramCustomerPropertyId.directAccessTherapy,
    [
      PathwayOptions.SELF_SEARCH,
      PathwayOptions.CHILD_SEARCH,
      PathwayOptions.COUPLES_THERAPY,
      PathwayOptions.FAMILY_THERAPY,
    ],
  ],
  [ProgramCustomerPropertyId.considerMeds, [PathwayOptions.MEDICATION_CONSULTATION]],
  [ProgramCustomerPropertyId.blendedCareMeds, [PathwayOptions.MEDICATION_CONSULTATION]],
])

/**
 * Maps pathway to program customer properties. The pathway is available if any of the customer properties are true
 */
export const PATHWAY_TO_PROGRAM_CUSTOMER_PROPERTY_MAP = new Map<ExtendedPathwayOption, ProgramCustomerPropertyId[]>([
  [
    PathwayOptions.SELF_SEARCH,
    [ProgramCustomerPropertyId.considerLyraTherapy, ProgramCustomerPropertyId.directAccessTherapy],
  ],
  [PathwayOptions.CHILD_SEARCH, [ProgramCustomerPropertyId.directAccessTherapy]],
  [
    PathwayOptions.MEDICATION_CONSULTATION,
    [ProgramCustomerPropertyId.considerMeds, ProgramCustomerPropertyId.blendedCareMeds],
  ],
  [PathwayOptions.COUPLES_THERAPY, [ProgramCustomerPropertyId.directAccessTherapy]],
  [PathwayOptions.FAMILY_THERAPY, [ProgramCustomerPropertyId.directAccessTherapy]],
])
